import { useMemo } from 'react';
import { Card } from '../../common/Card';
import { AnimatePresence, motion } from 'framer-motion';
import { getCategoryIcon, getResourceTypeLabel } from '../../../data/resources';

const categoryLabels = {
  minerals: 'الموارد المعدنية',
  energy: 'مصادر الطاقة',
  agriculture: 'الزراعة',
  tourism: 'السياحة'
};

const listVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 }
};

export const ResourceExplorerControl = ({
  resources,
  activeCategories,
  selectedResourceId,
  searchQuery,
  onSearchChange,
  onResourceSelect,
  showResources,
  onToggleResources,
  availableTypes,
  availableGovernorates,
  activeTypes,
  activeGovernorates,
  minImportance,
  onToggleType,
  onToggleGovernorate,
  onChangeImportance
}) => {
  const totalCount = resources.length;

  const categoryCounts = useMemo(() => {
    return activeCategories.reduce((acc, category) => {
      acc[category] = resources.filter((item) => item.category === category).length;
      return acc;
    }, {});
  }, [resources, activeCategories]);

  const activeFiltersCount = activeTypes.length + activeGovernorates.length + (minImportance > 1 ? 1 : 0);

  return (
    <Card className="p-4 space-y-4 map-panel-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span>📍</span>
          <h3 className="text-lg font-black">موارد الخريطة</h3>
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold">
          <input
            type="checkbox"
            checked={showResources}
            onChange={() => onToggleResources(!showResources)}
            className="w-4 h-4"
          />
          عرض الموارد
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
        <div className="map-soft-box">
          إجمالي النقاط: <span className="number font-bold">{totalCount}</span>
        </div>
        <div className="map-soft-box">
          الفلاتر النشطة: <span className="number font-bold">{activeFiltersCount}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {activeCategories.map((category) => (
          <span
            key={category}
            className="map-filter-pill"
          >
            {categoryLabels[category]}: {categoryCounts[category] ?? 0}
          </span>
        ))}
      </div>

      <input
        type="text"
        value={searchQuery}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="ابحث عن مورد..."
        className="input-text h-10 text-sm"
      />

      <details className="rounded-2xl border border-sky-100 bg-white/70 p-3 group">
        <summary className="cursor-pointer text-sm font-semibold text-gray-700 flex items-center justify-between">
          <span>فلترة متقدمة</span>
          <span className="text-[10px] text-gray-500">{activeFiltersCount} فلتر</span>
        </summary>
        <div className="mt-3 space-y-3">
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-600">حسب النوع</p>
            <div className="flex flex-wrap gap-2">
              {availableTypes.map((type) => {
                const isActive = activeTypes.includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => onToggleType(type)}
                    className={`map-filter-pill ${isActive ? 'map-filter-pill-active' : ''}`}
                  >
                    {getResourceTypeLabel(type)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-600">حسب المحافظة</p>
            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto pr-1">
              {availableGovernorates.map((governorate) => {
                const isActive = activeGovernorates.includes(governorate);
                return (
                  <button
                    key={governorate}
                    type="button"
                    onClick={() => onToggleGovernorate(governorate)}
                    className={`map-filter-pill ${isActive ? 'map-filter-pill-active' : ''}`}
                  >
                    {governorate}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-gray-600">
              <span>الحد الأدنى للأهمية</span>
              <span>{'⭐'.repeat(minImportance)}</span>
            </div>
            <input
              type="range"
              min="1"
              max="5"
              value={minImportance}
              onChange={(event) => onChangeImportance(Number(event.target.value))}
              className="w-full"
            />
          </div>
        </div>
      </details>

      <div className="flex items-center justify-between text-xs font-semibold text-gray-600">
        <span>النتائج</span>
        <span className="number">{totalCount}</span>
      </div>

      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {resources.length === 0 ? (
          <p className="text-sm text-gray-500">لا توجد موارد مطابقة للبحث.</p>
        ) : (
          <AnimatePresence mode="popLayout">
            {resources.map((resource) => (
              <motion.button
                layout
                key={resource.id}
                type="button"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={listVariants}
                transition={{ duration: 0.2 }}
                onClick={() => onResourceSelect(resource)}
                className={`w-full text-right rounded-2xl p-3 space-y-1 transition border ${
                  selectedResourceId === resource.id
                    ? 'border-pink-300 bg-pink-50/70'
                    : 'border-sky-100 bg-white hover:border-cyan-300'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getCategoryIcon(resource.type)}</span>
                    <p className="text-sm font-semibold">{resource.name}</p>
                  </div>
                  <span className="text-xs text-gray-500">{categoryLabels[resource.category]}</span>
                </div>
                <p className="text-xs text-gray-500">
                  {resource.governorate} · {'⭐'.repeat(resource.importance)}
                </p>
              </motion.button>
            ))}
          </AnimatePresence>
        )}
      </div>
    </Card>
  );
};
